import { useState, useCallback, useMemo } from 'react';
import ChatService from '../services/chatService';

/**
 * Custom hook for managing ticket list state and operations
 * Consolidates filtering, sorting, and CRUD operations
 */
export const useTicketList = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState(new Set());
  const [analytics, setAnalytics] = useState(null);

  // Load tickets
  const loadTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await ChatService.getAllTickets();
      if (result.success) {
        setTickets(result.data || []);
      } else {
        setError('Failed to load tickets');
      }
    } catch (err) {
      console.error('Failed to load tickets:', err);
      setError(err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load analytics
  const loadAnalytics = useCallback(async () => {
    try {
      setError(null);
      const result = await ChatService.getTicketAnalytics();
      if (result.success) {
        setAnalytics(result.data);
      } else {
        setError('Failed to load analytics');
      }
    } catch (err) {
      console.error('Failed to load analytics:', err);
      setError(err.message || 'Failed to load analytics');
    }
  }, []);

  // Update ticket
  const updateTicket = useCallback(async (ticketId, updates) => {
    try {
      setError(null);
      const result = await ChatService.updateTicket(ticketId, updates);
      if (result.success) {
        setTickets(prev =>
          prev.map(t => t.id === ticketId ? { ...t, ...updates } : t)
        );
      } else {
        setError('Failed to update ticket');
      }
      return result;
    } catch (err) {
      console.error('Failed to update ticket:', err);
      setError(err.message || 'Failed to update ticket');
      return { success: false };
    }
  }, []);

  // Bulk update status
  const bulkUpdateStatus = useCallback(async (ticketIds, newStatus) => {
    try {
      setError(null);
      const results = await Promise.all(
        ticketIds.map(id => ChatService.updateTicket(id, { status: newStatus }))
      );

      const allSuccessful = results.every(r => r.success);
      if (allSuccessful) {
        setTickets(prev =>
          prev.map(t =>
            ticketIds.includes(t.id) ? { ...t, status: newStatus } : t
          )
        );
        setSelectedTickets(new Set());
      } else {
        setError('Some tickets failed to update');
      }
      return { success: allSuccessful };
    } catch (err) {
      console.error('Failed to bulk update:', err);
      setError(err.message || 'Failed to update tickets');
      return { success: false };
    }
  }, []);

  // Memoized filtered and sorted tickets
  const filteredTickets = useMemo(() => {
    let result = tickets;

    // Apply status filter
    if (filter !== 'all') {
      result = result.filter(ticket => ticket.status === filter);
    }

    // Apply search with sanitization
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(ticket =>
        (ticket.ticket_number?.toLowerCase().includes(query)) ||
        (ticket.title?.toLowerCase().includes(query)) ||
        (ticket.user?.full_name?.toLowerCase().includes(query)) ||
        (ticket.user?.email?.toLowerCase().includes(query)) ||
        (Array.isArray(ticket.tags) && 
         ticket.tags.some(tag => tag.toLowerCase().includes(query)))
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      const priorityOrder = { urgent: 4, high: 3, normal: 2, low: 1 };
      
      if (sortBy === 'priority') {
        return (priorityOrder[b.priority] || 0) - (priorityOrder[a.priority] || 0);
      } else if (sortBy === 'updated') {
        return new Date(b.updated_at) - new Date(a.updated_at);
      }
      // Default: 'created'
      return new Date(b.created_at) - new Date(a.created_at);
    });

    return result;
  }, [tickets, filter, sortBy, searchQuery]);

  return {
    tickets,
    filteredTickets,
    loading,
    error,
    filter,
    setFilter,
    sortBy,
    setSortBy,
    searchQuery,
    setSearchQuery,
    selectedTickets,
    setSelectedTickets,
    analytics,
    loadTickets,
    loadAnalytics,
    updateTicket,
    bulkUpdateStatus
  };
};
